import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSubscription } from '../../lib/featureFlags';

/**
 * lot DK2 — mise en page du menu latéral, utilisée par TOUTES les pages à menu (17 appels
 * identiques dans `App.js` auparavant).
 *  · < 768 px   : menu caché, ouvert par le ☰ de la barre du haut (prop `ouvert`).
 *  · 768-1279 px : colonne d'icônes de 64 px ; le ☰, une rubrique à sous-menu ou l'avatar
 *    déplient le menu complet PAR-DESSUS la page (voile sombre, fermeture au voile ou par Échap).
 *  · ≥ 1280 px  : menu ouvert en 288 px, comme avant.
 * Le `<Sidebar>` reste l'unique source des rubriques : il est passé en enfant et reçoit
 * `onClose` et `sousMenuOuvert`.
 */
const ENTREES = [
  { cle: 'avis', titre: 'Avis de Valeur', icone: 'bi-calculator', chemin: '/outils/avis-de-valeur', module: 'avis-de-valeur' },
  { cle: 'tableau', titre: 'Tableau de bord', icone: 'bi-speedometer2', action: 'showDashboardView' },
  { cle: 'mon-stock', titre: 'Mon Stock', icone: 'bi-shop', prefixes: ['/properties', '/biens', '/diffusion'] },
  { cle: 'contacts-crm', titre: 'Contacts & CRM', icone: 'bi-people', prefixes: ['/contacts', '/demandes-contact', '/groupes'] },
  { cle: 'documents', titre: 'Documents', icone: 'bi-folder-fill', prefixes: ['/documents'] },
  { cle: 'marketing', titre: 'Marketing', icone: 'bi-megaphone', prefixes: ['/marketing'] },
  { cle: 'statistiques', titre: 'Statistiques', icone: 'bi-graph-up', prefixes: ['/observatoire', '/outils/simulateurs'] },
];

export const CadreMenu = ({ ouvert, onFermer, children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: abonnement } = useSubscription();
  const modules = abonnement?.subscription?.active_modules || null;
  const [deplie, setDeplie] = useState(false);
  const [sousMenu, setSousMenu] = useState(null);

  const enfant = React.Children.only(children);
  const complet = ouvert || deplie;

  const fermer = React.useCallback(() => {
    setDeplie(false);
    setSousMenu(null);
    onFermer?.();
  }, [onFermer]);

  useEffect(() => {
    if (!complet) return undefined;
    const touche = (e) => { if (e.key === 'Escape') fermer(); };
    window.addEventListener('keydown', touche);
    return () => window.removeEventListener('keydown', touche);
  }, [complet, fermer]);

  const chemin = location.pathname || '';
  const active = (e) => (e.chemin ? chemin.startsWith(e.chemin)
    : (e.prefixes || []).some((p) => chemin.startsWith(p)));

  const cliquer = (e) => {
    if (e.chemin) { navigate(e.chemin); return; }
    if (e.action) { enfant.props[e.action]?.(); return; }
    setSousMenu(e.cle);
    setDeplie(true);
  };

  const initiales = `${(enfant.props.userProfile?.prenom || '').charAt(0)}${(enfant.props.userProfile?.nom || '').charAt(0)}`.toUpperCase() || '?';

  return (
    <>
      {/* Tablette : colonne d'icônes */}
      <div className="hidden md:flex xl:hidden w-16 shrink-0 h-screen flex-col items-center gap-1 py-3 bg-[#0F172A] border-r border-slate-800"
           data-testid="sidebar-rail">
        <button
          type="button"
          onClick={() => { setSousMenu(null); setDeplie(true); }}
          data-testid="sidebar-deplier"
          title="Ouvrir le menu"
          aria-label="Ouvrir le menu"
          className="w-11 h-11 rounded-lg text-white hover:bg-slate-800 flex items-center justify-center"
        >
          <i className="bi bi-list text-2xl"></i>
        </button>
        <div className="w-8 h-px bg-slate-800 my-1" />
        {ENTREES.filter((e) => !e.module || modules === null || modules.includes(e.module)).map((e) => (
          <button
            key={e.cle}
            type="button"
            onClick={() => cliquer(e)}
            title={e.titre}
            aria-label={e.titre}
            data-testid={`sidebar-rail-${e.cle}`}
            className={`w-11 h-11 rounded-lg flex items-center justify-center transition ${
              active(e) ? 'bg-blue-600 text-white' : 'text-white hover:bg-slate-800'
            }`}
          >
            <i className={`bi ${e.icone} text-xl`}></i>
          </button>
        ))}
        <button
          type="button"
          onClick={() => { setSousMenu(null); setDeplie(true); }}
          title="Mon compte"
          aria-label="Mon compte"
          data-testid="sidebar-rail-compte"
          className="mt-auto w-11 h-11 rounded-full bg-slate-700 text-white text-xs font-bold flex items-center justify-center"
        >
          {initiales}
        </button>
      </div>

      {/* Menu complet : par-dessus la page jusqu'à 1 279 px, en colonne à partir de 1 280 px */}
      <div className={complet
        ? 'fixed inset-y-0 left-0 z-50 w-[85%] sm:w-[75%] md:w-[320px]'
        : 'hidden xl:block xl:relative xl:w-72'}>
        {React.cloneElement(enfant, { onClose: fermer, sousMenuOuvert: sousMenu })}
      </div>
      {complet && (
        <div className="fixed inset-0 bg-black/50 z-40 xl:hidden" onClick={fermer} />
      )}
    </>
  );
};

export default CadreMenu;
